import React from "react";
import {
  List,
  useTable,
  EditButton,
  DeleteButton,
} from "@refinedev/antd";
import type { BaseRecord } from "@refinedev/core";
import {
  Table,
  Space,
  Tag,
  Typography,
  Badge,
  Tooltip,
  Switch,
  message,
} from "antd";
import {
  TrophyOutlined,
  CheckCircleOutlined,
  StopOutlined,
} from "@ant-design/icons";
import { supabaseClient } from "../../utility/supabaseClient";

const { Text } = Typography;

export const MembershipTierList: React.FC = () => {
  const { tableProps } = useTable({
    sorters: {
      initial: [{ field: "display_order", order: "asc" }],
    },
  });

  // 切换启用/禁用状态
  const toggleActive = async (id: BaseRecord["id"], currentStatus: boolean) => {
    if (!id || typeof id !== "string") {
      message.error("无效的等级ID");
      return;
    }

    try {
      const { error } = await supabaseClient
        .from("membership_tiers")
        .update({ is_active: !currentStatus })
        .eq("id", id);

      if (error) {
        message.error(`更新失败: ${error.message}`);
        return;
      }

      message.success(currentStatus ? "已禁用该等级" : "已启用该等级");
      window.location.reload();
    } catch (err: any) {
      message.error(`更新异常: ${err.message}`);
    }
  };

  // 格式化价格（分 → 元）
  const formatPrice = (cents: number) => {
    return (cents / 100).toFixed(2);
  };

  // 渲染功能权限标签
  const renderFeatures = (features: any) => {
    if (!features) return <Text type="secondary">无</Text>;

    const featureList = [];
    if (features.hd_quality) featureList.push("高清");
    if (features["4k_quality"]) featureList.push("4K");
    if (features.ad_free) featureList.push("无广告");
    if (features.offline_download) featureList.push("离线下载");
    if (features.early_access) featureList.push("抢先看");

    return (
      <Space size={4} wrap>
        {featureList.map((feature) => (
          <Tag key={feature} color="blue" style={{ margin: 2 }}>
            {feature}
          </Tag>
        ))}
        {featureList.length === 0 && <Text type="secondary">基础功能</Text>}
      </Space>
    );
  };

  return (
    <List title="订阅等级管理">
      <Table {...tableProps} rowKey="id">
        <Table.Column
          dataIndex="display_order"
          title="排序"
          width={80}
          render={(value: number) => (
            <Badge
              count={value}
              style={{ backgroundColor: "#1890ff" }}
              showZero
            />
          )}
        />
        <Table.Column
          dataIndex="display_name"
          title="等级名称"
          render={(value: string, record: any) => (
            <Space>
              {record.name === "vip" && (
                <TrophyOutlined style={{ color: "#faad14" }} />
              )}
              <Text strong>{value}</Text>
              <Text type="secondary">({record.name})</Text>
            </Space>
          )}
        />
        <Table.Column
          dataIndex="price_cents"
          title="价格"
          render={(value: number, record: any) => (
            <Space direction="vertical" size={0}>
              <Text strong style={{ fontSize: 16 }}>
                ¥{formatPrice(value)}
              </Text>
              {record.original_price_cents &&
                record.original_price_cents > value && (
                  <Text delete type="secondary" style={{ fontSize: 12 }}>
                    原价 ¥{formatPrice(record.original_price_cents)}
                  </Text>
                )}
            </Space>
          )}
        />
        <Table.Column
          dataIndex="duration_days"
          title="时长"
          render={(value: number) => <Text>{value} 天</Text>}
        />
        <Table.Column
          dataIndex="features"
          title="功能权限"
          render={(features: any) => renderFeatures(features)}
        />
        <Table.Column
          dataIndex="badge_text"
          title="徽章"
          render={(value?: string, record?: any) =>
            value ? (
              <Tag
                color={record?.badge_color || "#1890ff"}
                style={{
                  borderRadius: 4,
                  padding: "2px 8px",
                }}
              >
                {value}
              </Tag>
            ) : (
              <Text type="secondary">-</Text>
            )
          }
        />
        <Table.Column
          dataIndex="is_active"
          title="状态"
          render={(value: boolean, record: any) => (
            <Tooltip title={value ? "点击禁用" : "点击启用"}>
              <Switch
                checked={value}
                checkedChildren={<CheckCircleOutlined />}
                unCheckedChildren={<StopOutlined />}
                onChange={() => toggleActive(record.id, value)}
              />
            </Tooltip>
          )}
        />
        <Table.Column
          title="操作"
          dataIndex="actions"
          render={(_, record: BaseRecord) => (
            <Space>
              <EditButton hideText size="small" recordItemId={record.id} />
              {record.name !== "free" && (
                <DeleteButton hideText size="small" recordItemId={record.id} />
              )}
            </Space>
          )}
        />
      </Table>
    </List>
  );
};
